/**
 * Hook real para egresos usando Firebase
 */

import { useState, useEffect } from 'react';
import type { Egreso } from '../../../shared/types';
import { EgresoFirebaseRepository } from '../services/egreso-firebase.repository';
import { useAuth } from '../../auth/hooks/useAuth';
import { LoggerService } from '../../../shared/services/logger.service';

const egresoRepository = new EgresoFirebaseRepository();

export const useEgresosFirebase = () => {
  const { user } = useAuth();
  const [egresos, setEgresos] = useState<Egreso[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  console.log('🎯 useEgresosFirebase - Hook ejecutándose:', { 
    userId: user?.uid, 
    loading, 
    egresosCount: egresos.length 
  });

  useEffect(() => {
    const userId = user?.uid;
    
    console.log('🔄 useEgresosFirebase - useEffect INICIANDO:', { 
      userId,
      hasUser: !!userId
    });

    if (!userId) {
      console.log('❌ useEgresosFirebase - No hay usuario, saliendo');
      setLoading(false);
      setEgresos([]);
      return;
    }

    console.log('✅ useEgresosFirebase - Usuario válido, continuando con suscripción');

    setLoading(true);
    setError(null);
    
    console.log('🚀 useEgresosFirebase - Iniciando suscripción al repository con userId:', userId);
    LoggerService.info('Inicializando suscripción a egresos Firebase', 'useEgresosFirebase', { userId });

    const unsubscribe = egresoRepository.subscribeToEgresos(
      userId,
      (egresosActualizados) => {
        console.log('🔥 useEgresosFirebase - Callback recibido:', { count: egresosActualizados.length });
        setEgresos(egresosActualizados);
        setLoading(false);
        setError(null);
      }
    );

    return () => {
      LoggerService.info('Cerrando suscripción a egresos Firebase', 'useEgresosFirebase', { userId });
      unsubscribe();
    };
  }, [user?.uid]);

  const createEgreso = async (egresoData: Omit<Egreso, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => {
    if (!user?.uid) {
      throw new Error('Usuario no autenticado');
    }

    setError(null);

    try {
      LoggerService.info('Creando nuevo egreso', 'useEgresosFirebase', { 
        userId: user.uid,
        concepto: egresoData.concepto,
        monto: egresoData.monto
      });

      const id = await egresoRepository.createEgreso(user.uid, egresoData);
      
      LoggerService.logUserAction('egreso_created_hook', 'useEgresosFirebase', { 
        userId: user.uid,
        egresoId: id,
        monto: egresoData.monto,
        categoria: egresoData.categoria
      });

      return id;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al crear egreso';
      setError(errorMessage);
      LoggerService.error('Error creando egreso', 'useEgresosFirebase', { 
        userId: user.uid,
        error: errorMessage
      });
      throw error;
    }
  };

  const updateEgreso = async (id: string, egresoData: Partial<Omit<Egreso, 'id' | 'userId' | 'createdAt'>>) => {
    if (!user?.uid) {
      throw new Error('Usuario no autenticado');
    }

    setError(null);

    try {
      LoggerService.info('Actualizando egreso', 'useEgresosFirebase', { 
        userId: user.uid,
        egresoId: id
      });

      await egresoRepository.updateEgreso(user.uid, id, egresoData);
      
      LoggerService.logUserAction('egreso_updated_hook', 'useEgresosFirebase', { 
        userId: user.uid,
        egresoId: id
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al actualizar egreso';
      setError(errorMessage);
      LoggerService.error('Error actualizando egreso', 'useEgresosFirebase', { 
        userId: user.uid,
        egresoId: id,
        error: errorMessage
      });
      throw error;
    }
  };

  const deleteEgreso = async (id: string) => {
    if (!user?.uid) {
      throw new Error('Usuario no autenticado');
    }

    setError(null);

    try {
      LoggerService.info('Eliminando egreso', 'useEgresosFirebase', { 
        userId: user.uid,
        egresoId: id
      });

      await egresoRepository.deleteEgreso(user.uid, id);
      
      LoggerService.logUserAction('egreso_deleted_hook', 'useEgresosFirebase', { 
        userId: user.uid,
        egresoId: id
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al eliminar egreso';
      setError(errorMessage);
      LoggerService.error('Error eliminando egreso', 'useEgresosFirebase', { 
        userId: user.uid,
        egresoId: id,
        error: errorMessage
      });
      throw error;
    }
  };

  // Función de utilidad para migrar datos mock
  const migrateMockData = async (mockEgresos: Omit<Egreso, 'userId'>[]) => {
    if (!user?.uid) {
      throw new Error('Usuario no autenticado');
    }

    setError(null);

    try {
      LoggerService.info('Iniciando migración de datos mock', 'useEgresosFirebase', { 
        userId: user.uid,
        count: mockEgresos.length
      });

      await egresoRepository.migrateMockData(user.uid, mockEgresos);
      
      LoggerService.logUserAction('mock_data_migrated_hook', 'useEgresosFirebase', { 
        userId: user.uid,
        type: 'egresos',
        count: mockEgresos.length
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error en migración de datos';
      setError(errorMessage);
      LoggerService.error('Error en migración de datos mock', 'useEgresosFirebase', { 
        userId: user.uid,
        error: errorMessage
      });
      throw error;
    }
  };

  // Funciones de utilidad para cálculos
  const getTotalEgresos = () => {
    return egresos.reduce((total, egreso) => total + egreso.monto, 0);
  };

  const getEgresosPorCategoria = () => {
    const categorias: { [key: string]: number } = {};
    egresos.forEach(egreso => {
      categorias[egreso.categoria] = (categorias[egreso.categoria] || 0) + egreso.monto;
    });
    return categorias;
  };

  const getEgresosPorMes = (year: number, month: number) => {
    return egresos.filter(egreso => {
      const fecha = new Date(egreso.fecha);
      return fecha.getFullYear() === year && fecha.getMonth() === month;
    });
  };

  return {
    egresos,
    loading,
    error,
    createEgreso,
    updateEgreso,
    deleteEgreso,
    migrateMockData,
    getTotalEgresos,
    getEgresosPorCategoria,
    getEgresosPorMes
  };
};